import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, MapPin, Euro, Shield, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getLocalBusinessSchema, getFAQSchema, getBreadcrumbSchema } from '@/utils/seoSchemas';

export default function FribourgGestion() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Quels quartiers de Fribourg sont les plus rentables pour la location saisonnière ?",
      answer: "La Vieille-Ville (Bourg, Auge, Neuveville) est la plus prisée pour son charme médiéval (200 façades gothiques, 14 ponts, funiculaire historique unique). Pérolles séduit pour sa modernité et accessibilité. La Basse-Ville offre authenticité et proximité Sarine. Fribourg attire désormais 500k+ nuitées/an."
    },
    {
      question: "Quelle est la réglementation pour l'Airbnb à Fribourg ?",
      answer: "À Fribourg (canton FR), la réglementation cantonale encadre la parahôtellerie (forte croissance : +5.2% nuitées). Il est recommandé de vous renseigner auprès de la commune pour les obligations de déclaration. Vérifiez les restrictions de votre PPE."
    },
    {
      question: "Combien coûte votre service de conciergerie à Fribourg ?",
      answer: "Notre tarification est de 22% de commission sur les revenus locatifs + 80 CHF/mois de frais de gestion. Ce tarif inclut l'ensemble des services (annonce, pricing, ménages, check-in/out, maintenance)."
    },
    {
      question: "Proposez-vous des photos professionnelles ?",
      answer: "Oui, nous incluons un shooting photo professionnel dans notre prestation pour maximiser l'attractivité de votre annonce."
    }
  ];

  const schemas = [
    getLocalBusinessSchema('Fribourg', 'Vaud', 46.802, 7.151),
    getFAQSchema(faqs),
    getBreadcrumbSchema([
      { name: 'Accueil', url: 'https://homely-conciergerie.ch' },
      { name: 'Gestion Locative', url: 'https://homely-conciergerie.ch/nos-villes' },
      { name: 'Fribourg', url: 'https://homely-conciergerie.ch/gestion-locative/fribourg' }
    ])
  ];

  return (
    <Layout>
      <SEOHead
        title="Gestion Location Saisonnière Fribourg | Conciergerie Airbnb"
        description="Conciergerie Airbnb et gestion locative saisonnière à Fribourg. Commission 22% + 80 CHF/mois. Service complet : annonce, pricing dynamique, ménages, check-in, maintenance."
        canonical="https://homely-conciergerie.ch/gestion-locative/fribourg"
        schemas={schemas}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.7), rgba(43, 43, 43, 0.85)), url('https://images.pexels.com/photos/33771825/pexels-photo-33771825.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Gestion Locative Saisonnière<br />
              <span className="text-[#8A9474]">Fribourg</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-4 leading-relaxed">
              Conciergerie Airbnb complète pour appartements, maisons et villas
            </p>
            
            <p className="text-lg mb-8 text-gray-200">
              Ville bilingue | Centre médiéval | Université
            </p>

            <Button 
              onClick={() => navigate('/estimation')}
              size="lg"
              className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-4 md:px-10 py-6 md:py-7 text-sm md:text-lg font-semibold whitespace-normal md:whitespace-nowrap"
              data-testid="hero-cta-estimation"
            >
              Estimer Votre Bien à Fribourg
            </Button>
          </div>
        </section>

        {/* Marché Local */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <MapPin className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Le Marché de la Location Saisonnière à Fribourg
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center mb-6">
              <div className="prose prose-lg max-w-none text-[#4A4A4A] space-y-4">
                <p>
                  Située à la frontière linguistique (français-allemand), Fribourg bénéficie d'une attractivité exceptionnelle portée par son patrimoine médiéval : 200 façades gothiques, 14 ponts historiques, fortifications ouvertes au public et le funiculaire unique au monde (contrepoids à eaux usées reliant ville haute/basse). Le canton a franchi le cap des 500 000 nuitées hôtelières en 2025, confirmant une dynamique robuste.
                </p>
                <p>
                  La Vieille-Ville (Bourg, Auge, Neuveville) concentre l'essentiel de la demande pour son authenticité et ses ruelles pavées. Pérolles attire pour sa modernité et son accessibilité. La Basse-Ville séduit les visiteurs en quête d'ambiance locale (bord de Sarine). La parahôtellerie (dont Airbnb) est en forte croissance (+5.2% nuitées) et couvre désormais 102 des 119 communes fribourgeoises. La carte d'hôte digitale (depuis avril 2026) offre transports zone 10 gratuits.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.pexels.com/photos/33771825/pexels-photo-33771825.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Fribourg ville médiévale bilingue - Airbnb centre historique" 
                  className="w-full h-[300px] object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Shield className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Nos Services de Conciergerie à Fribourg
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Création d'annonce optimisée", desc: "Photos professionnelles, description SEO, mise en valeur des atouts de votre bien" },
                { title: "Pricing dynamique", desc: "Ajustement quotidien des tarifs selon la demande locale et les événements" },
                { title: "Gestion des réservations", desc: "Communication avec les voyageurs, validation des profils, gestion du calendrier" },
                { title: "Ménages professionnels", desc: "Prestataires qualifiés, contrôle qualité, rotation rapide entre les séjours" },
                { title: "Accueil des voyageurs", desc: "Check-in/check-out flexible, remise des clés, explications du logement" },
                { title: "Maintenance & interventions", desc: "Réseau d'artisans locaux, gestion des incidents, entretien préventif" },
                { title: "Reporting mensuel", desc: "Suivi des revenus, statistiques d'occupation, recommandations d'optimisation" },
                { title: "Service client 7j/7", desc: "Équipe locale disponible pour vous et vos voyageurs" }
              ].map((service, idx) => (
                <Card key={`idx-${idx}`} className="border-2 border-[#E5E2DB]">
                  <CardContent className="p-6">
                    <CheckCircle2 className="h-6 w-6 text-[#8A9474] mb-3" />
                    <h4 className="font-bold text-[#2B2B2B] mb-2">{service.title}</h4>
                    <p className="text-sm text-[#4A4A4A]">{service.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Tarification */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Euro className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Tarification Transparente
            </h2>
            
            <Card className="border-2 border-[#8A9474]">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-[#8A9474] mb-2">22% + 80 CHF/mois</div>
                  <div className="text-sm text-[#4A4A4A]">Commission sur revenus locatifs + frais de gestion</div>
                </div>
                <ul className="space-y-3 text-[#4A4A4A]">
                  {[
                    'Aucun frais caché',
                    'Tous les services inclus',
                    'Paiement mensuel garanti',
                    'Sans engagement de durée minimum'
                  ].map((item, i) => (
                    <li key={`i-${i}`} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Réglementation */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-6">
              <Calendar className="inline h-8 w-8 text-[#8A9474] mr-2" />
              Réglementation Locale
            </h2>
            
            <div className="bg-[#E8EBE4] p-6 rounded-xl">
              <p className="text-[#4A4A4A] mb-4">
                À Fribourg (canton FR), la parahôtellerie (dont Airbnb) est en forte croissance. Il est recommandé de vous renseigner auprès de la commune de Fribourg pour connaître les obligations de déclaration et de taxe de séjour.
              </p>
              <p className="text-[#4A4A4A]">
                Certains règlements de copropriété (PPE) peuvent imposer des restrictions spécifiques. Nous vous recommandons de vérifier votre statut auprès du service du logement de la ville de Fribourg et de consulter le règlement de votre PPE.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center">
              Questions Fréquentes
            </h2>
            
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={`idx-${idx}`} className="border-2 border-[#E5E2DB]">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-[#2B2B2B] mb-3">{faq.question}</h4>
                    <p className="text-[#4A4A4A]">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 px-6 bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Prêt à Confier Votre Bien à Fribourg ?
            </h2>
            <p className="text-xl mb-8 text-gray-200">
              Recevez une estimation gratuite de vos revenus locatifs potentiels
            </p>
            <Button 
              onClick={() => navigate('/estimation')}
              size="lg"
              className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-4 md:px-10 py-6 md:py-7 text-sm md:text-lg font-semibold whitespace-normal md:whitespace-nowrap"
              data-testid="cta-final-estimation"
            >
              Obtenir Mon Estimation Gratuite
            </Button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
